/**
 * DeviceActivityDock — makes headless filesystem access visible.
 *
 * The device agent runs in Electron main with no window required, so the
 * agent can read and write the user's files while nothing on screen says
 * so. The audit trail records it, but an audit trail is something you
 * consult *after* wondering — it does not tell you as it happens.
 *
 * This dock does. It floats above the app, is draggable so it never
 * covers what the user is working on, and stays out of the way until
 * there is something to show: idle with no history it renders nothing at
 * all, rather than becoming a permanent widget the user learns to ignore.
 *
 * Desktop-only by construction — it subscribes through the Electron
 * bridge, so on web and mobile `getDesktopBridge()` is null and the
 * component is inert.
 */
import * as React from "react";
import { getDesktopBridge } from "@omni/client-platform";
import type { DeviceActivity, DeviceState } from "@omni/client-platform";

const MAX_ROWS = 6;
const IDLE_HIDE_MS = 8_000;
const STORAGE_KEY = "omni:device-dock-position";

interface Row extends DeviceActivity {
  at: number;
}

/** Turn an MCP method into something readable at a glance. */
function label(method: string): string {
  const verb = method.replace(/^tools\/call$/, "call").replace(/_/g, " ");
  return verb.length > 40 ? `${verb.slice(0, 39)}…` : verb;
}

function loadPosition(): { x: number; y: number } {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as { x: number; y: number };
  } catch {
    /* private mode / blocked storage — fall through to the default */
  }
  return { x: 24, y: 96 };
}

export function DeviceActivityDock() {
  const bridge = getDesktopBridge();
  const [rows, setRows] = React.useState<Row[]>([]);
  const [state, setState] = React.useState<DeviceState["state"]>("unenrolled");
  const [pos, setPos] = React.useState(loadPosition);
  const dragRef = React.useRef<{ dx: number; dy: number } | null>(null);

  React.useEffect(() => {
    if (!bridge) return;
    const offActivity = bridge.onDeviceActivity((a) => {
      setRows((prev) => {
        // Collapse start -> finish onto one row, so a single file read
        // does not read as two unrelated events.
        const without = prev.filter((r) => r.id !== a.id);
        return [{ ...a, at: Date.now() }, ...without].slice(0, MAX_ROWS);
      });
    });
    const offState = bridge.onDeviceState((s) => setState(s.state));
    void bridge.deviceStatus().then((s) => {
      setState(s.connected ? "connected" : s.enrolled ? "disconnected" : "unenrolled");
    });
    return () => {
      offActivity();
      offState();
    };
  }, [bridge]);

  // Clear finished rows after a quiet spell, so the dock disappears
  // rather than pinning the last thing that happened on screen forever.
  React.useEffect(() => {
    if (!rows.length) return;
    const t = window.setTimeout(() => {
      setRows((prev) => (prev.some((r) => r.phase === "started") ? prev : []));
    }, IDLE_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [rows]);

  const onPointerDown = (e: React.PointerEvent) => {
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Clamped so the dock can never be dragged off-screen and lost.
    setPos({
      x: Math.max(0, Math.min(window.innerWidth - 240, e.clientX - drag.dx)),
      y: Math.max(0, Math.min(window.innerHeight - 80, e.clientY - drag.dy)),
    });
  };
  const onPointerUp = () => {
    dragRef.current = null;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      /* not worth failing a drag over */
    }
  };

  // Nothing to say, so nothing on screen.
  if (!bridge || (!rows.length && state !== "connected")) return null;

  const busy = rows.some((r) => r.phase === "started");

  return (
    <div
      className="fixed z-50 w-60 border border-border bg-bg-elevated shadow-lg select-none"
      style={{ left: pos.x, top: pos.y }}
      role="status"
      aria-live="polite"
      aria-label="Device activity"
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b border-border cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <span
          className={[
            "inline-block h-2 w-2",
            busy
              ? "bg-accent animate-pulse"
              : state === "connected"
                ? "bg-success"
                : "bg-fg-faint",
          ].join(" ")}
          aria-hidden
        />
        <span className="text-xs font-medium text-fg">
          {busy ? "Working on this device" : "This device"}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-fg-faint">
          {state === "connected" ? "live" : state}
        </span>
      </div>

      {rows.length > 0 && (
        <ul className="max-h-48 overflow-auto">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-border last:border-b-0"
              title={r.error ?? r.method}
            >
              <span
                className={[
                  "inline-block h-1.5 w-1.5 shrink-0",
                  r.phase === "started"
                    ? "bg-accent animate-pulse"
                    : r.phase === "failed"
                      ? "bg-danger"
                      : "bg-success",
                ].join(" ")}
                aria-hidden
              />
              <span className="truncate text-fg-muted">{label(r.method)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
